<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createAzureFilesConfig } from "./fs";
  import { makeFileSystemAzureFiles, testAzureFilesConnection } from "./fs-azurefiles";
  import { captureEvent, telegramNotify } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let config = $state(createAzureFilesConfig());
  let testingConnection = $state(false);
  let errorMessage = $state("");

  let canConnect = $derived(
    config.account.trim() != "" && config.key.trim() != "" && config.share.trim() != "" && !testingConnection,
  );

  async function onConnect() {
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("testing azure files connection");
    try {
      config.account = config.account.trim().toLowerCase();
      config.key = config.key.trim();
      config.share = config.share.trim();
      await testAzureFilesConnection(config);
      if (config.name.trim() == "") {
        config.name = config.share;
      } else {
        config.name = config.name.trim();
      }
      let fs = makeFileSystemAzureFiles(config);
      let who = config.name;
      telegramNotify(`New Azure Files connection for ${who}`);
      captureEvent("azurefiles-login", {});
      removeMsg();
      mountfs(fs);
      open = false;
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect Azure Files">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-azurefiles-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-azurefiles-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <label class="mt-3" for="input-azurefiles-account">Storage account:</label>
      <input
        type="text"
        id="input-azurefiles-account"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.account}
      />

      <label class="mt-3" for="input-azurefiles-key">Account key:</label>
      <input
        type="password"
        id="input-azurefiles-key"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.key}
      />

      <label class="mt-3" for="input-azurefiles-share">File share:</label>
      <input
        type="text"
        id="input-azurefiles-share"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.share}
      />

      <div class="mt-4 text-sm">
        <a
          href="https://learn.microsoft.com/azure/storage/common/storage-account-keys-manage#view-account-access-keys"
          class="text-blue-600 underline"
          target="_blank"
          rel="noreferrer">Account keys</a
        >
      </div>

      {#if errorMessage}
        <div class="mt-2 mb-2 text-red-500 font-medium break-all w-72">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect Azure Files</button>
    </div>
  {/snippet}
</DialogBase>
